import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API, recordVisit } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  heardForm, wordVariants, disambiguateVariants, muqattaatIn, collapseMuqattaat, advanceStream,
} from "../lib/quranMatch";
import BackLink from "../components/BackLink";
import Recorder from "../components/Recorder";
import MistakeHeatmap from "../components/MistakeHeatmap";
import MadaniPage from "../components/MadaniPage";
import {
  CheckCircle2, ArrowRight, Award, RefreshCcw, Brain, BookOpen, Repeat,
  Sparkles, Mic, Send, AlertTriangle, Headphones, Eye, Settings as SettingsIcon,
  BookMarked, AlignJustify, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { useBasicHifz, goToPlans } from "@/lib/hifzScope";

/* Hifz Test runs on any surah the plan has open (Basic Hifz, then the ladder steps). The server enforces that; the pickers
   below only offer what the plan allows. */
const AVAILABLE_SURAHS = new Set(Array.from({ length: 114 }, (_, i) => i + 1));

/* ---------------- MODE PICKER (first screen) ---------------- */

/* ---------------- Mode picker landing ---------------- */
function ModePicker({ onPick }) {
  const { tx, t: tr } = useLanguage();
  const cards = [
    { id: "memorize", label: "Memorize new", icon: BookOpen, desc: "Open the Hifz Plan to start fresh ayahs.", tone: "forest", to: "/app/hifz" },
    { id: "review", label: "Review old", icon: Repeat, desc: "Revisit memorized ayahs via the Qari loop.", tone: "gold", to: "/app/qari" },
    { id: "test_whisper", label: "AI recitation test", icon: Headphones, desc: "Recite the full range in one take — your AI coach scores Hifz + Tajweed and explains every mistake.", tone: "terracotta" },
    { id: "test_peek", label: "Self test & practice", icon: Brain, desc: "All ayahs hidden — reveal one at a time or hover to peek, or tap the mic to recite live for instant feedback.", tone: "gold" },
  ];
  const toneClasses = {
    forest: "border-forest-600/30 hover:bg-forest-600/5",
    gold: "border-gold-500/40 hover:bg-gold-500/5",
    terracotta: "border-terracotta-500/40 hover:bg-terracotta-500/5",
  };
  const iconTone = { forest: "text-forest-700", gold: "text-gold-600", terracotta: "text-terracotta-600" };
  return (
    <div className="space-y-6" data-testid="hifz-mode-picker">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{tr("hifzTest.welcomeToYourHifzDashboard")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{tr("hifzTest.whatAreWeDoingToday")}</h1>
        <p className="mt-2 text-foreground/70 max-w-2xl">{tr("hifzTest.pickAFlowAssalamuAlaikum")}</p>
      </header>
      <div className="grid gap-4">
        {cards.map((c) => {
          const Inner = (
            <div className={`hifz-card cursor-pointer transition border ${toneClasses[c.tone]}`}>
              <div className="flex items-start gap-4">
                <div className={`h-11 w-11 rounded-xl bg-white grid place-items-center ${iconTone[c.tone]}`}>
                  <c.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display font-semibold text-lg text-forest-700">{tx(c.label)}</div>
                  <p className="text-sm text-foreground/70 mt-1">{tx(c.desc)}</p>
                </div>
              </div>
            </div>
          );
          return c.to ? (
            <Link to={c.to} key={c.id} data-testid={`mode-${c.id}`}>{Inner}</Link>
          ) : (
            <button key={c.id} type="button" onClick={() => onPick(c.id)} data-testid={`mode-${c.id}`} className="text-left">
              {Inner}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Range picker (shared) ---------------- */
function RangeControls({ surahs, surah, setSurah, data, page, setPage, pageOptions, pageAyahs, ayahStart, setAyahStart, ayahEnd, setAyahEnd, onReset }) {
  const { t: tr } = useLanguage();
  const hs = useBasicHifz();
  const total = Math.max(0, ayahEnd - ayahStart + 1);
  // Free plan: only the surahs (and, below, the ayahs) that Basic Hifz covers.
  const visibleSurahs = surahs.filter((s) => AVAILABLE_SURAHS.has(s.number) && hs.surahAllowed(s.number));
  // Narrow the From/To options to just this page's ayahs once a page is
  // picked, so "choose a page, then choose an ayah" actually narrows things
  // down instead of still listing the whole surah.
  const ayahOptions = (pageAyahs?.length
    ? (data?.ayahs || []).filter((a) => pageAyahs.includes(a.n))
    : (data?.ayahs || [])).filter((a) => hs.inScope(surah, a.n));
  return (
    <div className="hifz-card space-y-3">
      <div className="flex flex-wrap gap-3 items-center">
        <label className="text-sm text-muted-foreground">{tr("hifzTest.surah")}</label>
        <select value={surah} onChange={(e) => { const n = parseInt(e.target.value); const first = hs.firstAyah(n); setSurah(n); setAyahStart(first); setAyahEnd(first); }}
          className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm" data-testid="hifz-test-surah">
          {visibleSurahs.map((s) => <option key={s.number} value={s.number}>{`${s.number} · ${s.name_en}`}</option>)}
        </select>
        {pageOptions?.length > 0 && (
          <>
            <label className="text-sm text-muted-foreground">{tr("hifzTest.page")}</label>
            <select value={page || ""} onChange={(e) => setPage(parseInt(e.target.value))}
              className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm" data-testid="hifz-test-page">
              {pageOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <label className="text-sm text-muted-foreground">{tr("myQuran.from")}</label>
        <select value={ayahStart} onChange={(e) => { const n = parseInt(e.target.value); setAyahStart(n); if (n > ayahEnd) setAyahEnd(n); }}
          className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm" data-testid="hifz-test-start">
          {ayahOptions.map((a) => <option key={a.n} value={a.n}>{a.n}</option>)}
        </select>
        <label className="text-sm text-muted-foreground">{tr("myQuran.to")}</label>
        <select value={ayahEnd} onChange={(e) => setAyahEnd(parseInt(e.target.value))}
          className="rounded-xl border border-sand-300 bg-white px-4 py-2 text-sm" data-testid="hifz-test-end">
          {ayahOptions.filter((a) => a.n >= ayahStart && hs.rangeInScope(surah, ayahStart, a.n)).map((a) => <option key={a.n} value={a.n}>{a.n}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">{tr("common.ayahCount", { count: total })}</span>
        <button type="button" onClick={onReset} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs border border-sand-300 hover:bg-sand-100" data-testid="hifz-test-reset">
          <RefreshCcw className="h-3.5 w-3.5" /> {tr("hifzTest.reset")}
        </button>
      </div>
    </div>
  );
}


/* ---------------- Madani-layout helpers ---------------- */
/* useAyahPage: look up which Madani page contains a given (surah, ayah).
   Returns {pageNo, loading}. Cached in module memory so toggling modes
   doesn't refetch. */
const _ayahPageCache = new Map();
function useAyahPage(surah, ayah) {
  const key = `${surah}:${ayah}`;
  const [pageNo, setPageNo] = useState(() => _ayahPageCache.get(key) || null);
  const [loading, setLoading] = useState(!_ayahPageCache.has(key));
  useEffect(() => {
    if (!surah || !ayah) return;
    if (_ayahPageCache.has(key)) { setPageNo(_ayahPageCache.get(key)); setLoading(false); return; }
    setLoading(true);
    api.get(`/quran/ayah-page/${surah}/${ayah}`)
      .then((r) => { _ayahPageCache.set(key, r.data.page); setPageNo(r.data.page); })
      .catch(() => setPageNo(null))
      .finally(() => setLoading(false));
  }, [surah, ayah, key]);
  return { pageNo, loading };
}

/* Build statusByVerseWord for MadaniPage overlay from a per-ayah array of
   per-word statuses. perAyahStatuses[i] is the array of statuses for the
   i-th ayah in rangeAyahs (length = number of canonical words). */
function buildStatusMap(surah, rangeAyahs, perAyahStatuses) {
  const map = {};
  rangeAyahs.forEach((a, i) => {
    const statuses = perAyahStatuses[i] || [];
    statuses.forEach((s, j) => {
      if (!s) return;
      map[`${surah}:${a.n}/${j + 1}`] = s;
    });
  });
  return map;
}

/* Tiny shared toggle so all 3 modes use the same layout choice. */
function LayoutToggle({ layout, onChange }) {
  const { t: tr } = useLanguage();
  return (
    <div className="inline-flex gap-1 p-1 rounded-full bg-sand-200 border border-sand-300" data-testid="layout-toggle">
      <button
        onClick={() => onChange("flow")}
        data-testid="layout-flow"
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition ${
          layout === "flow" ? "bg-forest-600 text-sand-100" : "text-foreground/70 hover:text-foreground"
        }`}
      >
        <AlignJustify className="h-3 w-3" /> {tr("hifzTest.flow")}
      </button>
      <button
        onClick={() => onChange("madani")}
        data-testid="layout-madani"
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition ${
          layout === "madani" ? "bg-forest-600 text-sand-100" : "text-foreground/70 hover:text-foreground"
        }`}
      >
        <BookMarked className="h-3 w-3" /> {tr("tadabur.madani")}
      </button>
    </div>
  );
}


/* ---------------- LIVE ASR MODE — Tarteel-style hidden mushaf ---------------- */
// Detach a recognizer's handlers BEFORE aborting it, so cancelling one of our own
// sessions (restart, unmount, React StrictMode's double effect) never surfaces as
// a "Speech error: aborted" toast. Chrome allows only one live recognizer, so a
// stray one left running would abort the next one that starts.
function killRecognition(rec) {
  if (!rec) return;
  rec.onresult = null; rec.onerror = null; rec.onend = null;
  try { rec.abort(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
}

const SPEECH_ERROR_TEXT = {
  "not-allowed": "Microphone access is blocked — allow it in your browser's site settings, then try again.",
  "service-not-allowed": "Speech recognition is blocked in this browser — allow the microphone and try again.",
  "audio-capture": "No microphone found, or another app is using it.",
  network: "Can't reach the speech service — check your internet connection.",
  "language-not-supported": "This browser can't recognise Arabic speech. Try Chrome or Edge.",
  aborted: "Speech recognition was interrupted — tap Resume to continue.",
};

function LiveAsrMode({ rangeAyahs, surah, autoAdvanceMs, layout = "flow", autoStart = false }) {
  const { t: tr, tx } = useLanguage();
  // Build TWO parallel structures:
  // - wordList: matchable Arabic words (stop signs / verse-number markers filtered out)
  // - displayAyahs: rendering-friendly tokens per ayah with optional matchIdx → wordList index
  const { wordList, displayAyahs } = useMemo(() => {
    const list = [];
    const ayahs = rangeAyahs.map((a) => {
      const tokens = a.ar.trim().split(/\s+/);
      let posInAyah = 0;
      const display = tokens.map((tok) => {
        // Every legitimate spelling of the Mushaf word (see quranMatch.wordVariants).
        const variants = wordVariants(tok);
        if (!variants.length) return { word: tok, matchIdx: null }; // stop sign / marker — never matched, always visible
        posInAyah += 1;
        const matchIdx = list.length;
        list.push({ ayah: a.n, posInAyah, word: tok, variants });
        return { word: tok, matchIdx };
      });
      return { n: a.n, tokens: display };
    });
    // A spelling shared by two different words of the range only counts for one of them.
    disambiguateVariants(list.map((w) => w.variants)).forEach((v, i) => { list[i].variants = v; });
    return { wordList: list, displayAyahs: ayahs };
  }, [rangeAyahs]);
  // Disjoined-letter words in the range (الٓمٓ), which ASR spells out letter by letter.
  const muqattaat = useMemo(
    () => muqattaatIn(rangeAyahs.flatMap((a) => a.ar.trim().split(/\s+/))),
    [rangeAyahs],
  );

  const [supported, setSupported] = useState(true);
  const [running, setRunning] = useState(false);
  const [revealedIdx, setRevealedIdx] = useState(0); // next word index expected
  const [errorAt, setErrorAt] = useState(null); // {idx, heard}
  const [timeline, setTimeline] = useState([]); // {ts, expected, said}
  const [skippedRanges, setSkippedRanges] = useState([]); // [{ayah, fromIdx, toIdx}]
  const [finalTranscript, setFinalTranscript] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const startTimeRef = useRef(null);
  const recogRef = useRef(null);
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const mountedRef = useRef(true);
  const startTokenRef = useRef(0); // bumped on every start(); a stale start aborts itself
  const autoStartedRef = useRef(false);
  const userStoppedRef = useRef(false); // Stop tapped: don't auto-restart the recognizer
  const fatalErrorRef = useRef(false);  // mic blocked etc.: restarting can't help
  const restartsRef = useRef(0);
  const lastTokensLenRef = useRef(0);   // heard tokens in the current session (incl. held ones)

  // Which words were NOT recited right. Flagged-wrong words that later auto-advance
  // (or get corrected) stay flagged — they must never turn green like a clean word —
  // and a skipped ayah counts every one of its words, not one mistake. One word can be
  // in several lists (e.g. missed in silence), hence Sets.
  const { mistakeIdx, skippedIdx, notRightCount } = useMemo(() => {
    const skipped = new Set();
    skippedRanges.forEach((r) => { for (let i = r.fromIdx; i <= r.toIdx; i++) skipped.add(i); });
    timeline.forEach((m) => { if (m.skipped && m.idx != null) skipped.add(m.idx); });
    const mistakes = new Set();
    timeline.forEach((m) => { if (!m.skipped && m.idx != null && !skipped.has(m.idx)) mistakes.add(m.idx); });
    const inRange = (i) => i >= 0 && i < wordList.length;
    return {
      mistakeIdx: mistakes,
      skippedIdx: skipped,
      notRightCount: new Set([...mistakes, ...skipped].filter(inRange)).size,
    };
  }, [timeline, skippedRanges, wordList]);

  // Madani overlay — fetch the page that contains the first ayah of the range,
  // then build a positional status map from revealedIdx / errorAt / skipped.
  const { pageNo: madaniPageNo } = useAyahPage(surah, rangeAyahs[0]?.n);
  const asrHiddenAyahsSet = useMemo(
    () => (layout === "madani" ? new Set(rangeAyahs.map((a) => `${surah}:${a.n}`)) : null),
    [layout, rangeAyahs, surah],
  );
  const asrStatusByVerseWord = useMemo(() => {
    if (layout !== "madani") return {};
    const map = {};
    wordList.forEach((w, idx) => {
      const key = `${surah}:${w.ayah}/${w.posInAyah}`;
      if (errorAt && errorAt.idx === idx) map[key] = "wrong";
      else if (idx < revealedIdx && skippedIdx.has(idx)) map[key] = "missed";
      else if (idx < revealedIdx && mistakeIdx.has(idx)) map[key] = "wrong";
      else if (idx < revealedIdx) map[key] = "ok";
    });
    return map;
  }, [layout, wordList, revealedIdx, errorAt, skippedIdx, mistakeIdx, surah]);

  useEffect(() => {
    mountedRef.current = true;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) setSupported(false);
    return () => {
      mountedRef.current = false;
      killRecognition(recogRef.current);
      try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
      try { audioStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
    };
  }, []);

  // Reset state when range changes
  useEffect(() => {
    setRevealedIdx(0); setErrorAt(null); setTimeline([]); setFinalTranscript("");
    setSkippedRanges([]); setAudioUrl(null); setAudioBlob(null);
    errorSinceRef.current = null;
  }, [rangeAyahs]);

  const start = async () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }

    // Only ever one live session: cancel any recognizer/recorder from an earlier
    // start (a leftover one makes Chrome abort the new one with "aborted").
    const token = ++startTokenRef.current;
    killRecognition(recogRef.current);
    recogRef.current = null;
    if (mediaRecRef.current) {
      mediaRecRef.current.onstop = null;
      try { mediaRecRef.current.state === "recording" && mediaRecRef.current.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
      mediaRecRef.current = null;
    }
    try { audioStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
    audioStreamRef.current = null;

    // Parallel audio capture so the student can replay before submitting.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (token !== startTokenRef.current || !mountedRef.current) {
        // A newer start() (or unmount) happened while the permission prompt was open.
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      audioStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size > 0) {
          setAudioBlob(blob);
          setAudioUrl(URL.createObjectURL(blob));
        }
        try { audioStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
        audioStreamRef.current = null;
      };
      mediaRecRef.current = mr;
      mr.start();
    } catch (e) {
      // Mic permission denied — keep going with Web Speech (which has its own mic prompt path)
      toast.message(tr("hifzTest.recordingPlaybackUnavailableContinuing"));
    }
    if (token !== startTokenRef.current || !mountedRef.current) return;

    userStoppedRef.current = false;
    fatalErrorRef.current = false;
    restartsRef.current = 0;
    revealedIdxRef.current = revealedIdx;
    startTimeRef.current = Date.now();
    lastProgressAt.current = Date.now();
    try {
      startRecognition(SR);
      setRunning(true);
    } catch (err) { console.error("[hifz-test] mic start failed:", err); toast.error(tr("hifzTest.couldnTStartMicrophone")); }
  };

  // One recognizer session. Chrome ends a continuous session on its own (after a
  // pause, or ~60s) — mid-recitation — so onend restarts it transparently unless
  // the student tapped Stop, the range is finished, or a real error happened.
  const startRecognition = (SR) => {
    const rec = new SR();
    rec.lang = "ar-SA";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.onresult = (event) => {
      // The whole session's speech, every result in order, so token positions stay
      // stable across phrases. (Reading only from event.resultIndex restarted the
      // token list at each new phrase while consumedRef still counted the previous
      // one — the first words recited after every pause were silently skipped.)
      const parts = [];
      for (let i = 0; i < event.results.length; i++) parts.push(event.results[i][0].transcript);
      const transcript = parts.join(" ");
      setFinalTranscript(transcript);

      const heard = transcript.trim().split(/\s+/).map(heardForm).filter(Boolean);
      const tokens = collapseMuqattaat(heard, muqattaat).tokens;
      lastTokensLenRef.current = tokens.length;
      const wordNorms = wordList.map((w) => w.variants);
      const prevCursor = revealedIdxRef.current;
      const prevConsumed = consumedRef.current;
      // While the phrase is still interim its last word may be half-spoken;
      // advanceStream leaves it unjudged/unconsumed until it's complete.
      const lastResult = event.results[event.results.length - 1];
      const { cursor, consumed, mistakes, pending } = advanceStream(
        wordNorms, tokens, revealedIdxRef.current, consumedRef.current,
        { lastTokenProvisional: !lastResult?.isFinal },
      );
      consumedRef.current = consumed;
      revealedIdxRef.current = cursor;
      setRevealedIdx(cursor);
      // Any positive signal — new tokens heard, cursor advanced, or a fresh
      // mistake registered — resets the silence timer.
      if (cursor !== prevCursor || tokens.length !== prevConsumed || mistakes.length) {
        lastProgressAt.current = Date.now();
      }
      if (mistakes.length) {
        const ts = Date.now() - startTimeRef.current;
        setTimeline((prev) => {
          const seen = new Set(prev.filter((m) => !m.skipped).map((m) => m.idx));
          const add = [];
          mistakes.forEach((m) => {
            if (seen.has(m.idx)) return;
            seen.add(m.idx);
            add.push({ ts, expected: wordList[m.idx]?.word || "", said: m.heard || "(skipped)", idx: m.idx });
          });
          return add.length ? [...prev, ...add] : prev;
        });
      }
      if (pending) {
        if (errorAtRef.current?.idx !== pending.idx) errorSinceRef.current = Date.now();
        setErrorAt({ ...pending, expected: wordList[pending.idx]?.word || "" });
      } else {
        setErrorAt(null);
        errorSinceRef.current = null;
      }
      if (cursor >= wordList.length) {
        try { rec.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
        try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
      }
    };
    rec.onerror = (e) => {
      const code = e.error || "unknown";
      // Our own aborts detach their handlers first (killRecognition), so an error
      // reaching here is a real one. A pause ("no-speech") just ends the session,
      // which onend restarts; anything else can't be fixed by restarting.
      if (code === "no-speech") return;
      fatalErrorRef.current = true;
      toast.error(SPEECH_ERROR_TEXT[code] ? tx(SPEECH_ERROR_TEXT[code]) : tr("hifzTest.speechError", { code }));
    };
    rec.onend = () => {
      if (recogRef.current !== rec) return;
      const done = revealedIdxRef.current >= wordList.length;
      if (!done && !userStoppedRef.current && !fatalErrorRef.current && mountedRef.current && restartsRef.current < 30) {
        restartsRef.current += 1;
        consumedRef.current = 0;              // a new session starts a new result list
        lastTokensLenRef.current = 0;
        try { startRecognition(SR); return; } catch (err) { console.debug("[hifz-test] restart failed:", err); }
      }
      setRunning(false);
    };
    recogRef.current = rec;
    consumedRef.current = 0;
    lastTokensLenRef.current = 0;
    rec.start();
  };
  const stop = () => {
    userStoppedRef.current = true;
    try { recogRef.current?.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
    try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
    setRunning(false);
  };

  // M3 — when this mode is opened via the merged self-test's mic button,
  // recording starts immediately (one tap = switch view + start reciting)
  // rather than landing here and requiring a second "Start reciting" tap.
  useEffect(() => {
    // Guard against React StrictMode's dev-only double effect, which would start
    // two recognizers at once (Chrome aborts one → "Speech error: aborted").
    if (autoStart && !autoStartedRef.current) { autoStartedRef.current = true; start(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refs for closures (so onresult sees latest state without re-creating recognition)
  const revealedIdxRef = useRef(0);
  const consumedRef = useRef(0);
  const errorAtRef = useRef(null);
  const errorSinceRef = useRef(null); // wall-clock ms when the current error first appeared
  // `lastProgressAt` is the wall-clock of the LAST positive signal from the
  // recognizer — either a new transcript token arrived, the cursor advanced,
  // or a fresh mistake was registered. Used by the silence-detect auto-advance.
  const lastProgressAt = useRef(null);
  useEffect(() => { revealedIdxRef.current = revealedIdx; }, [revealedIdx]);
  useEffect(() => { errorAtRef.current = errorAt; }, [errorAt]);

  // Auto-advance: when an error has been STUCK on the same word for `autoAdvanceMs`,
  // force-mark it wrong and move forward. Keyed on `errorAt?.idx` (not the whole
  // errorAt object) so that fresh mismatch tokens at the same word do NOT restart
  // the timer mid-stream. Uses errorSinceRef so the timeout always fires at the
  // first-seen wall clock + autoAdvanceMs.
  useEffect(() => {
    const idx = errorAt?.idx;
    if (idx == null || !autoAdvanceMs || autoAdvanceMs <= 0) return undefined;
    if (errorSinceRef.current == null) errorSinceRef.current = Date.now();
    const elapsed = Date.now() - errorSinceRef.current;
    const remaining = Math.max(50, autoAdvanceMs - elapsed);
    const timer = setTimeout(() => {
      if (errorAtRef.current?.idx !== idx) return; // user moved on already
      setRevealedIdx((cur) => {
        if (cur !== idx) return cur;
        revealedIdxRef.current = cur + 1;
        return cur + 1;
      });
      setErrorAt(null);
      errorSinceRef.current = null;
    }, remaining);
    return () => clearTimeout(timer);
  }, [errorAt?.idx, autoAdvanceMs]);

  // Silence auto-advance: when the recognizer is running but no transcript
  // tokens have arrived for `autoAdvanceMs`, treat the current word as missed —
  // flash it red, log it, and advance the cursor. Without this, a silent
  // student would hang on the expected word forever.
  useEffect(() => {
    if (!running || !autoAdvanceMs || autoAdvanceMs <= 0) return undefined;
    const tick = setInterval(() => {
      if (errorAtRef.current) return; // wrong-word path already handles it
      const idx = revealedIdxRef.current;
      if (idx >= wordList.length) return;
      const last = lastProgressAt.current || startTimeRef.current || Date.now();
      if (Date.now() - last < autoAdvanceMs) return;
      // Silence exceeded the threshold — mark the current word missed & advance.
      const w = wordList[idx];
      if (!w) return;
      setErrorAt({ idx, expected: w.word, heard: "" });
      const ts = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
      setTimeline((prev) => [...prev, { ts, expected: w.word, said: "(silence)", idx }]);
      setSkippedRanges((prev) => [...prev, { ayah: w.ayah, fromIdx: idx, toIdx: idx }]);
      // Brief 250ms red flash so the student sees the mistake before we move on.
      setTimeout(() => {
        revealedIdxRef.current = idx + 1;
        consumedRef.current = lastTokensLenRef.current; // drop a half-heard leftover
        setRevealedIdx(idx + 1);
        setErrorAt(null);
        errorSinceRef.current = null;
        lastProgressAt.current = Date.now();
        if (idx + 1 >= wordList.length) {
          try { recogRef.current?.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
          try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
        }
      }, 250);
      // Reset progress marker so the timer doesn't re-fire instantly while
      // the flash is showing.
      lastProgressAt.current = Date.now();
    }, 200);
    return () => clearInterval(tick);
  }, [running, autoAdvanceMs, wordList]);

  // Find the matchIdx of the FIRST matchable word of the next ayah (or end of list)
  const advanceToNextAyah = () => {
    if (revealedIdx >= wordList.length) return;
    const currentAyahN = wordList[revealedIdx].ayah;
    // Find next index whose ayah differs
    let nextIdx = wordList.length;
    for (let i = revealedIdx; i < wordList.length; i++) {
      if (wordList[i].ayah !== currentAyahN) { nextIdx = i; break; }
    }
    if (nextIdx > revealedIdx) {
      setSkippedRanges((prev) => [...prev, { ayah: currentAyahN, fromIdx: revealedIdx, toIdx: nextIdx - 1 }]);
      setRevealedIdx(nextIdx);
      revealedIdxRef.current = nextIdx;
      consumedRef.current = lastTokensLenRef.current;
      setErrorAt(null);
      errorSinceRef.current = null;
      lastProgressAt.current = Date.now();
      const ts = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
      setTimeline((prev) => [...prev, { ts, expected: `(ayah ${currentAyahN})`, said: "[skipped]", idx: revealedIdx, skipped: true }]);
      if (nextIdx >= wordList.length) {
        try { recogRef.current?.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
        try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
      }
    }
  };

  // Skip just the current word (single token)
  const skipCurrentWord = () => {
    if (revealedIdx >= wordList.length) return;
    const w = wordList[revealedIdx];
    const ts = startTimeRef.current ? Date.now() - startTimeRef.current : 0;
    setTimeline((prev) => [...prev, { ts, expected: w.word, said: "[skipped]", idx: revealedIdx, skipped: true }]);
    setRevealedIdx(revealedIdx + 1);
    revealedIdxRef.current = revealedIdx + 1;
    consumedRef.current = lastTokensLenRef.current;
    setErrorAt(null);
    errorSinceRef.current = null;
    lastProgressAt.current = Date.now();
    if (revealedIdx + 1 >= wordList.length) {
      try { recogRef.current?.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
      try { mediaRecRef.current?.state === "recording" && mediaRecRef.current.stop(); } catch (err) { console.debug("[hifz-test] cleanup:", err); }
    }
  };

  const reset = () => {
    stop();
    setRevealedIdx(0); setErrorAt(null); setTimeline([]); setFinalTranscript("");
    setSkippedRanges([]);
    if (audioUrl) { try { URL.revokeObjectURL(audioUrl); } catch (err) { console.debug("[hifz-test] cleanup:", err); } }
    setAudioUrl(null); setAudioBlob(null);
    consumedRef.current = 0; revealedIdxRef.current = 0;
    errorSinceRef.current = null;
    lastProgressAt.current = null;
  };

  if (!supported) {
    return (
      <div className="hifz-card border border-terracotta-500/40" data-testid="asr-unsupported">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-terracotta-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-semibold text-terracotta-700">{tr("hifzTest.liveAsrNotSupported")}</div>
            <p className="text-sm text-foreground/75 mt-1">
              {tr("hifzTest.yourBrowserDoesnTSupport")} <strong>{tr("hifzTest.selfTest")}</strong> {tr("hifzTest.modeAbove")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const skipCount = timeline.filter((m) => m.skipped).length;
  const mistakeCount = timeline.length - skipCount;
  const completed = revealedIdx >= wordList.length && wordList.length > 0;
  // Share of words recited right. Never rounds up to 100% while any word is flagged.
  const accuracy = wordList.length === 0 ? 0
    : notRightCount === 0 ? 100
      : Math.min(99, Math.floor(((wordList.length - notRightCount) / wordList.length) * 100));
  const perfectRun = completed && notRightCount === 0;

  return (
    <div className="hifz-card space-y-5" data-testid="asr-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{tr("hifzTest.hiddenMushafLiveAsr")}</div>
          <p className="text-sm text-foreground/70 mt-1">{tr("hifzTest.reciteAloudWordsRevealIn")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!running ? (
            <button onClick={start} className="btn-primary text-sm" data-testid="asr-start">
              <Mic className="h-4 w-4" /> {revealedIdx > 0 ? tr("hifzTest.resume") : tr("hifzTest.startReciting")}
            </button>
          ) : (
            <button onClick={stop} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10" data-testid="asr-stop">
              <span className="h-2 w-2 rounded-full bg-terracotta-500 animate-pulse" /> {tr("hifzTest.stop")}
            </button>
          )}
          {revealedIdx > 0 && revealedIdx < wordList.length && (
            <>
              <button onClick={skipCurrentWord} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs border border-amber-500/40 text-amber-700 hover:bg-amber-500/10" data-testid="asr-skip-word">
                <ArrowRight className="h-3.5 w-3.5" /> {tr("hifzTest.skipWord")}
              </button>
              <button onClick={advanceToNextAyah} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs border border-forest-600/40 text-forest-700 hover:bg-forest-600/10" data-testid="asr-next-ayah">
                <ArrowRight className="h-3.5 w-3.5" /> {tr("myQuran.nextAyah")}
              </button>
            </>
          )}
          <button onClick={reset} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs border border-sand-300 hover:bg-sand-100" data-testid="asr-reset">
            <RefreshCcw className="h-3.5 w-3.5" /> {tr("hifzTest.reset")}
          </button>
        </div>
      </div>

      <div className="min-h-[120px] rounded-2xl border border-dashed border-sand-300 bg-sand-200/30 p-6 text-right" dir="rtl" data-testid="asr-mushaf">
        {layout === "madani" && madaniPageNo ? (
          <div dir="ltr" data-testid="asr-mushaf-madani">
            <MadaniPage pageNo={madaniPageNo} statusByVerseWord={asrStatusByVerseWord} hiddenAyahs={asrHiddenAyahsSet} />
          </div>
        ) : (
          <div className="ayah-arabic text-2xl md:text-3xl leading-[2.4]">
            {displayAyahs.map((a) => (
              <span key={a.n} data-testid={`asr-ayah-${a.n}`}>
                {a.tokens.map((t, i) => {
                  if (t.matchIdx === null) {
                    // Stop sign / verse-number marker — always visible, muted gold
                    return <span key={`${a.n}-marker-${i}`} className="inline-block mx-1 text-gold-600/60 select-none">{t.word}</span>;
                  }
                  const isRevealed = t.matchIdx < revealedIdx;
                  const isError = errorAt && errorAt.idx === t.matchIdx;
                  const isSkipped = skippedIdx.has(t.matchIdx);
                  const isMistake = mistakeIdx.has(t.matchIdx);
                  let cls = "opacity-0 select-none";
                  if (isRevealed && isSkipped) cls = "text-amber-700 underline decoration-dotted";
                  else if (isRevealed && isMistake) cls = "text-terracotta-600 underline decoration-dotted";
                  else if (isRevealed) cls = "text-emerald-700";
                  if (isError) cls = "text-terracotta-600 bg-terracotta-500/15 rounded px-1";
                  return (
                    <span key={`${a.n}-w-${t.matchIdx}`} className={`inline-block mx-1 transition ${cls}`}>{t.word}</span>
                  );
                })}
                <span className="text-gold-600 mx-1">﴿{a.n}﴾</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {errorAt && (
        <div className="rounded-xl border border-terracotta-500/40 bg-terracotta-500/10 px-4 py-3" data-testid="asr-error">
          <div className="text-[11px] uppercase tracking-[0.18em] text-terracotta-700 font-semibold">{tr("hifzTest.mistakeDetected")}</div>
          <div className="mt-1 text-sm text-foreground/85">
            {tr("hifzTest.expected")} <span className="ayah-arabic mx-1 text-emerald-700" dir="rtl">{errorAt.expected}</span>
            {tr("hifzTest.heard")} <span className="ayah-arabic mx-1 text-terracotta-600" dir="rtl">{errorAt.heard}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">{tr("hifzTest.tryAgainFromThisWord")} <em>{tr("hifzTest.skipWordNextAyah")}</em> {tr("hifzTest.toMoveOn")}</p>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground" data-testid="asr-progress">
        <span>{tr("hifzTest.wordsRevealed", { revealedIdx, wordListCount: wordList.length })}</span>
        <span>
          {tr("common.mistakeCount", { count: mistakeCount })}
          {skipCount > 0 && <span className="ml-2 text-amber-700">{tr("hifzTest.skipped", { skipCount })}</span>}
        </span>
      </div>

      {audioUrl && !running && (
        <div className="rounded-xl border border-sand-300 bg-white p-3" data-testid="asr-playback">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">{tr("hifzTest.yourRecitation")}</div>
          <audio controls src={audioUrl} className="w-full" data-testid="asr-playback-audio" />
        </div>
      )}

      {completed && (
        <div
          className={`rounded-2xl border p-5 text-center ${perfectRun ? "border-emerald-500/40 bg-emerald-50" : "border-amber-500/40 bg-amber-50"}`}
          data-testid="asr-complete"
        >
          <Award className={`h-8 w-8 mx-auto ${perfectRun ? "text-emerald-700" : "text-amber-700"}`} />
          <div className={`mt-2 font-display font-semibold text-xl ${perfectRun ? "text-emerald-700" : "text-amber-700"}`} data-testid="asr-complete-title">
            {perfectRun ? tr("hifzTest.mashallahRangeComplete") : tr("hifzTest.rangeFinishedReviewTheMarked")}
          </div>
          <p className="text-sm text-foreground/75 mt-1">
            {tr("hifzTest.wordsRight")} <strong>{accuracy}%</strong> &middot; {tr("common.mistakeCount", { count: mistakeCount })}
            {skipCount > 0 && <> {tr("hifzTest.skipped", { skipCount })}</>}
            {notRightCount > 0 && <> {tr("hifzTest.ofWordsNeedWork", { notRightCount, wordListCount: wordList.length })}</>}
          </p>
          <SubmitToTeacher
            mode="live_asr"
            timeline={timeline}
            audioBlob={audioBlob}
            summary={`Live ASR test · ${accuracy}% words right · ${wordList.length} words · ${notRightCount} not right (${mistakeCount} mistakes, ${skipCount} skipped)`}
            rangeAyahs={rangeAyahs}
            surah={surah}
          />
        </div>
      )}

      {finalTranscript && running && (
        <div className="text-xs text-muted-foreground bg-sand-200/40 rounded-xl px-3 py-2" data-testid="asr-transcript" dir="rtl">
          {finalTranscript}
        </div>
      )}
    </div>
  );
}

/* ---------------- Submit-to-teacher button (shared) ---------------- */
function SubmitToTeacher({ mode, timeline, summary, rangeAyahs, surah, audioBlob }) {
  const { t: tr } = useLanguage();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (sent) return <div className="mt-4 text-sm text-forest-700 font-medium">{tr("hifzTest.sentToYourTeacherFor")}</div>;
  const send = async () => {
    setSending(true);
    try {
      const first = rangeAyahs[0];
      const last = rangeAyahs[rangeAyahs.length - 1];
      const fd = new FormData();
      fd.append("surah", String(surah));
      fd.append("ayah", String(first.n));
      fd.append("ayah_end", String(last.n));
      fd.append("mode", mode);
      fd.append("timeline_count", String(timeline.length));
      if (audioBlob && audioBlob.size > 0) {
        const ext = (audioBlob.type.split("/")[1] || "webm").split(";")[0];
        fd.append("audio", audioBlob, `recitation.${ext}`);
      }
      const stubR = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recitation/test-stub`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!stubR.ok) throw new Error("Couldn't create stub");
      const stub = await stubR.json();
      await api.post("/recitation/submit", {
        attempt_id: stub.id,
        student_note: summary,
        mistakes_timeline: timeline,
        test_mode: mode,
        ai_summary: summary,
      });
      setSent(true);
      toast.success(tr("hifzTest.submittedToTeacher"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("hifzTest.couldnTSubmitMakeSure"));
    } finally { setSending(false); }
  };
  return (
    <button onClick={send} disabled={sending} className="mt-4 btn-accent text-sm" data-testid="hifz-submit-teacher">
      <Send className="h-4 w-4" /> {sending ? tr("hifzTest.sending") : tr("hifzTest.sendToMyTeacher")}
    </button>
  );
}

/* ---------------- SELF TEST & PRACTICE — merges the old separate "Self
   test" and "Quick practice" modes into one screen (UX report M3) ----------------

   Both BlindPeekMode and LiveAsrMode below are kept exactly as they were —
   LiveAsrMode in particular is the highest-complexity, highest-regression-risk
   part of this file (dual media pipeline, closure-workaround refs, a
   silence-based auto-advance timer), so this wrapper only changes how the two
   are PRESENTED, not their internals: one range picker, one card, and a mic
   button that switches between the two views. Tapping the mic both switches
   the view AND starts recording in the same tap (LiveAsrMode's `autoStart`),
   matching "tapping the mic starts a recording and moves straight into
   practice" rather than landing on a second screen with its own start button. */
function SelfTestMode({ rangeAyahs, surah, autoAdvanceMs, onChangeAutoAdvance, layout }) {
  const { t: tr } = useLanguage();
  const [live, setLive] = useState(false);
  useEffect(() => { setLive(false); }, [rangeAyahs]); // a fresh range starts back in the self-test view

  if (live) {
    return (
      <div className="space-y-4" data-testid="selftest-live-view">
        <button
          type="button"
          onClick={() => setLive(false)}
          className="text-xs text-forest-700 hover:underline inline-flex items-center gap-1.5"
          data-testid="selftest-back-to-peek"
        >
          <Brain className="h-3.5 w-3.5" /> {tr("hifzTest.backToSelfTestNo")}
        </button>
        <AutoAdvanceSettings value={autoAdvanceMs} onChange={onChangeAutoAdvance} />
        <LiveAsrMode rangeAyahs={rangeAyahs} surah={surah} autoAdvanceMs={autoAdvanceMs} layout={layout} autoStart />
      </div>
    );
  }
  return <BlindPeekMode rangeAyahs={rangeAyahs} surah={surah} layout={layout} onStartMic={() => setLive(true)} />;
}

/* ---------------- BLIND PEEK MODE — Hidden Mushaf canvas, peek button ---------------- */
function BlindPeekMode({ rangeAyahs, surah, layout = "flow", onStartMic }) {
  const { t: tr } = useLanguage();
  // How many ayahs of the range have been revealed so far, in order (UM3):
  // 0 = none, 1 = the first ayah, 2 = the first two, and so on.
  const [revealedCount, setRevealedCount] = useState(0);
  // The ayah currently under the pointer — revealed only while hovered (UM3).
  const [hoverAyah, setHoverAyah] = useState(null);
  const [finished, setFinished] = useState(false);
  useEffect(() => { setRevealedCount(0); setHoverAyah(null); setFinished(false); }, [rangeAyahs]);

  // One tap reveals the next ayah in sequence, rather than a Preview button
  // per ayah (which doesn't scale for long surahs — UX report M3/UM3).
  const revealNext = () => setRevealedCount((c) => Math.min(rangeAyahs.length, c + 1));

  // An ayah is visible if the sequential reveal has reached it, or the
  // pointer is currently over it.
  const isAyahVisible = (ayahN) => {
    const idx = rangeAyahs.findIndex((a) => a.n === ayahN);
    if (idx < 0) return false;
    return idx < revealedCount || hoverAyah === ayahN;
  };
  const allRevealed = revealedCount >= rangeAyahs.length;

  // Madani overlay — peeked words map to 'partial' status (amber)
  const { pageNo: madaniPageNo } = useAyahPage(surah, rangeAyahs[0]?.n);
  // Only ayahs that are still hidden go in the hidden set — a revealed or
  // hovered ayah drops out of it and renders normally again.
  const peekHiddenAyahsSet = useMemo(() => {
    if (layout !== "madani") return null;
    return new Set(
      rangeAyahs
        .filter((a, i) => !(i < revealedCount || hoverAyah === a.n))
        .map((a) => `${surah}:${a.n}`),
    );
  }, [layout, rangeAyahs, surah, revealedCount, hoverAyah]);
  const peekStatusByVerseWord = useMemo(() => {
    if (layout !== "madani") return {};
    const perAyah = rangeAyahs.map((a, i) => {
      const total = a.ar.trim().split(/\s+/).length;
      const visible = i < revealedCount || hoverAyah === a.n;
      return Array.from({ length: total }, () => (visible ? "partial" : null));
    });
    return buildStatusMap(surah, rangeAyahs, perAyah);
  }, [layout, rangeAyahs, surah, revealedCount, hoverAyah]);

  return (
    <div className="space-y-5" data-testid="peek-card">
      <div className="hifz-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{tr("hifzTest.hiddenMushafSelfTest")}</div>
            <p className="text-sm text-foreground/70 mt-1">
              {tr("hifzTest.allAyahsHiddenTap")} <strong>{tr("hifzTest.reveal")}</strong> {tr("hifzTest.toUncoverNextAyah")}
              {onStartMic && <> {tr("hifzTest.orTapThe")} <strong>{tr("hifzTest.mic")}</strong> {tr("hifzTest.toReciteItOutLoud")}</>}.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* M3 — merges what used to be a separate "Quick practice" mode
                into this one: tapping the mic switches straight into live
                ASR practice AND starts recording in the same tap (see
                SelfTestMode/LiveAsrMode's autoStart), rather than requiring
                the range to be re-picked on a different screen. */}
            {onStartMic && (
              <button
                onClick={onStartMic}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs border border-forest-600/40 text-forest-700 hover:bg-forest-600/10 font-semibold"
                data-testid="selftest-start-mic"
                title={tr("hifzTest.reciteThisRangeAloudRecording")}
              >
                <Mic className="h-3.5 w-3.5" /> {tr("hifzTest.reciteAloud")}
              </button>
            )}
            <div className="px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-700 text-xs font-semibold" data-testid="peek-counter">
              {tr("hifzTest.revealed")} <span className="font-bold">{revealedCount}</span>/{rangeAyahs.length}
            </div>
            <button
              onClick={revealNext}
              disabled={allRevealed}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs border border-amber-500/40 text-amber-700 hover:bg-amber-500/10 font-semibold disabled:opacity-40"
              data-testid="hifz-peek-reveal-all"
            >
              <Eye className="h-3.5 w-3.5" /> {allRevealed ? tr("hifzTest.allRevealed") : tr("hifzTest.reveal")}
            </button>
            <button onClick={() => setFinished(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs border border-sand-300 hover:bg-sand-100" data-testid="peek-finish-btn">
              <CheckCircle2 className="h-3.5 w-3.5" /> {tr("hifzTest.finishTest")}
            </button>
          </div>
        </div>

        {layout === "madani" && madaniPageNo ? (
          <div className="mt-6" data-testid="peek-mushaf-madani">
            <MadaniPage
              pageNo={madaniPageNo}
              statusByVerseWord={peekStatusByVerseWord}
              hiddenAyahs={peekHiddenAyahsSet}
              onAyahHover={(verseKey) => setHoverAyah(verseKey ? parseInt(verseKey.split(":")[1]) : null)}
            />
          </div>
        ) : (
          <div className="mt-6 min-h-[160px] rounded-2xl border border-dashed border-sand-300 bg-sand-200/30 p-6 text-right space-y-4" dir="rtl" data-testid="peek-mushaf">
            {rangeAyahs.map((a) => {
              const words = a.ar.trim().split(/\s+/);
              const visible = isAyahVisible(a.n);
              return (
                <div
                  key={a.n}
                  className="ayah-arabic text-2xl md:text-3xl leading-[2.4] rounded-lg transition hover:bg-amber-500/5"
                  data-testid={`peek-ayah-${a.n}`}
                  onMouseEnter={() => setHoverAyah(a.n)}
                  onMouseLeave={() => setHoverAyah((v) => (v === a.n ? null : v))}
                >
                  {words.map((w, i) => {
                    return (
                      <span key={`${a.n}-peek-${i}`} className={`inline-block mx-1 transition ${visible ? "text-amber-700" : "opacity-0 select-none pointer-events-none"}`}>{w}</span>
                    );
                  })}
                  <span className="text-gold-600 mx-1">﴿{a.n}﴾</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {finished && (
        <div className="hifz-card text-center" data-testid="hifz-revealed-section">
          <Award className="h-8 w-8 mx-auto text-emerald-700" />
          <div className="mt-2 font-display font-semibold text-emerald-700 text-xl">{tr("hifzTest.testComplete")}</div>
          <p className="text-sm text-foreground/75 mt-1">{tr("hifzTest.revealedOf", { revealed: revealedCount, count: rangeAyahs.length })}</p>
        </div>
      )}
    </div>
  );
}

/* ---------------- WHISPER HIDDEN MUSHAF — record once, AI scores both Hifz + Tajweed ---------------- */
function WhisperHiddenMushaf({ rangeAyahs, surah, ayahStart, ayahEnd, layout = "flow" }) {
  const { t: tr } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [revealedUpto, setRevealedUpto] = useState(0); // for the reveal sweep animation
  const [tips, setTips] = useState({}); // {globalIdx: tipString}
  const [tipBusy, setTipBusy] = useState({}); // {globalIdx: bool}
  const [note, setNote] = useState("");
  const [budget, setBudget] = useState(null); // {unlimited, limit_tests, used_tests, remaining_tests, per_test_seconds, resets_at}
  // Single-shot range flow: the student records the entire selected range in one
  // take, Whisper scores everything at once, and all ayahs reveal together via
  // the sweep animation. No per-ayah stop/advance.
  const [lastAudioUrl, setLastAudioUrl] = useState(null);
  // Teacher / halaqa picker — student can pick which enrolled halaqa to send to.
  // Falls back to "Unassigned" bucket on the backend if no class is selected.
  const [myClasses, setMyClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  useEffect(() => {
    api.get("/classes")
      .then((r) => {
        const enrolled = (r.data || []).filter((c) => c.is_enrolled);
        setMyClasses(enrolled);
        if (enrolled.length > 0 && !selectedClassId) setSelectedClassId(enrolled[0].class_id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setResult(null); setSubmitted(false); setRevealedUpto(0); setTips({}); setNote("");
    setLastAudioUrl(null);
  }, [surah, ayahStart, ayahEnd]);

  // Initial budget fetch + refresh after each successful Whisper run
  const refreshBudget = async () => {
    try {
      const r = await api.get("/recitation/whisper-budget");
      setBudget(r.data);
    } catch (err) { console.debug("[hifz-test] non-fatal:", err); }
  };
  useEffect(() => { refreshBudget(); }, []);

  // True once this month's AI tests are all used (10 a month in every package; they renew on the 1st).
  // Noor has the free self-test only; AI tests come with a package (10 a month).
  const noAiOnPlan = !!(budget && !budget.unlimited && budget.limit_tests === 0);
  const overQuota = !!(budget && !budget.unlimited && !noAiOnPlan && (budget.remaining_tests ?? Infinity) <= 0);

  // Reveal sweep animation: tick through words after result lands
  useEffect(() => {
    if (!result?.word_diff?.length) return undefined;
    setRevealedUpto(0);
    let i = 0;
    const step = Math.max(40, Math.min(140, 2000 / result.word_diff.length));
    const t = setInterval(() => {
      i += 1;
      setRevealedUpto(i);
      if (i >= result.word_diff.length) clearInterval(t);
    }, step);
    return () => clearInterval(t);
  }, [result]);

  const onAudio = async (blob, durationSeconds) => {
    setBusy(true); setSubmitted(false);
    try {
      const fd = new FormData();
      fd.append("surah", String(surah));
      fd.append("ayah", String(ayahStart));
      fd.append("ayah_end", String(ayahEnd));
      fd.append("audio", blob, "recitation.webm");
      fd.append("source", "hifz_test");
      fd.append("duration_seconds", String(durationSeconds || 0));
      const r = await fetch(`${API}/recitation/test`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!r.ok) {
        let msg = tr("hifzTest.recitationScoringFailed");
        try { const err = await r.json(); if (err?.detail) msg = typeof err.detail === "string" ? err.detail : err.detail.message || msg; } catch (err) { console.debug("[hifz-test] cleanup:", err); }
        if (r.status === 429) {
          refreshBudget();
        }
        throw new Error(msg);
      }
      const data = await r.json();
      refreshBudget();
      if (data.no_speech) {
        toast.error(data.feedback);
        return;
      }
      const url = URL.createObjectURL(blob);
      setLastAudioUrl(url);
      setResult(data);
      refreshBudget();
      const mistakeCount = (data.word_diff || []).filter((w) => w.status !== "ok").length;
      const rangeLabel = ayahStart === ayahEnd ? tr("hifzTest.ayah", { ayahStart }) : tr("hifzTest.ayahs", { ayahStart, ayahEnd });
      const extraCount = (data.extras || []).length;
      const extraNote = extraCount ? ` · ${tr("hifzTest.extraWordsHeardShort", { count: extraCount })}` : "";
      toast.success(
        mistakeCount === 0 && !extraCount
          ? tr("hifzTest.perfect", { rangeLabel, accuracy: data.accuracy })
          : `${rangeLabel} · ${data.accuracy}%${mistakeCount ? ` · ${tr("hifzTest.wordsToReview", { count: mistakeCount })}` : ""}${extraNote}`,
      );
    } catch (e) {
      toast.error(e?.message || tr("hifzTest.aiScoringFailed"));
    } finally { setBusy(false); }
  };

  const askWhy = async (globalIdx, expected, heard) => {
    if (tips[globalIdx] || tipBusy[globalIdx]) return;
    setTipBusy((b) => ({ ...b, [globalIdx]: true }));
    try {
      const r = await api.post("/recitation/tajweed-tip", {
        expected, heard: heard || "", surah, ayah: ayahStart,
      });
      setTips((t) => ({ ...t, [globalIdx]: r.data.tip }));
    } catch (err) {
      console.error("[hifz-test] tajweed-tip failed:", err);
      setTips((t) => ({ ...t, [globalIdx]: tr("hifzTest.couldnTFetchATip") }));
    } finally {
      setTipBusy((b) => ({ ...b, [globalIdx]: false }));
    }
  };

  const submitToTeacher = async () => {
    if (!result?.id) {
      toast.error(tr("hifzTest.recordTheRangeFirstBefore"));
      return;
    }
    setBusy(true);
    try {
      const totalMistakes = (result.word_diff || []).filter((w) => w.status !== "ok").length;
      const r = await api.post("/recitation/submit", {
        attempt_id: result.id,
        class_id: selectedClassId || null,
        student_note: note || `AI Recitation Test · range ${ayahStart}-${ayahEnd} · ${result.accuracy}% · ${totalMistakes} mistake${totalMistakes === 1 ? "" : "s"}`,
        test_mode: "single",
        ai_summary: result.feedback || "",
      });
      setSubmitted(true);
      if (r.data.already) toast.info(tr("hifzTest.youAlreadyHaveAPending"));
      else toast.success(selectedClassId ? tr("hifzTest.sentToYourSelectedTeacher") : tr("hifzTest.sentUnassignedPickAHalaqa"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("hifzTest.couldnTSubmitTryAgain"));
    } finally { setBusy(false); }
  };

  // Build per-ayah display map from result.word_diff (flat list keyed by global idx).
  // We also attach globalIdx to each entry so the mistake-list (below) can key
  // tipBusy / tips state per-word — previously globalIdx was only set here, leaving
  // the mistakes-list with `undefined` keys so clicking one Why? caused every row
  // to show "Thinking…".
  const wordDiffWithIdx = useMemo(
    () => (result?.word_diff || []).map((w, i) => ({ ...w, globalIdx: i })),
    [result],
  );
  const ayahDisplay = useMemo(() => {
    if (!wordDiffWithIdx.length) return [];
    let gi = 0;
    return rangeAyahs.map((a) => {
      const wordCount = a.ar.trim().split(/\s+/).length;
      const slice = [];
      for (let i = 0; i < wordCount && gi < wordDiffWithIdx.length; i++) {
        slice.push(wordDiffWithIdx[gi]);
        gi += 1;
      }
      return { n: a.n, words: slice };
    });
  }, [wordDiffWithIdx, rangeAyahs]);

  const totalWords = wordDiffWithIdx.length;
  const mistakes = wordDiffWithIdx.filter((w) => w.status !== "ok");
  const mistakeCount = mistakes.length;

  // Madani overlay status map: walk the full range's word_diff and reveal
  // progressively via the sweep animation. All ayahs in the range light up
  // together once recording is scored.
  const { pageNo: madaniPageNo } = useAyahPage(surah, rangeAyahs[0]?.n);
  // The Madani branch renders its own submit panel; the score card at the
  // bottom must skip its copy whenever this is true, or both show at once.
  const madaniActive = layout === "madani" && !!madaniPageNo;
  const statusByVerseWord = useMemo(() => {
    if (layout !== "madani" || !result?.word_diff?.length) return {};
    const map = {};
    let gi = 0;
    rangeAyahs.forEach((a) => {
      const wordCount = a.ar.trim().split(/\s+/).length;
      for (let j = 0; j < wordCount && gi < result.word_diff.length; j++) {
        if (gi < revealedUpto) {
          map[`${surah}:${a.n}/${j + 1}`] = result.word_diff[gi].status;
        }
        gi += 1;
      }
    });
    return map;
  }, [layout, rangeAyahs, result, revealedUpto, surah]);

  // Selected ayahs are hidden on the Madani page until they reveal via the sweep.
  // Non-selected ayahs on the same page stay fully visible (familiar context).
  const hiddenAyahsSet = useMemo(() => {
    if (layout !== "madani") return null;
    return new Set(rangeAyahs.map((a) => `${surah}:${a.n}`));
  }, [layout, rangeAyahs, surah]);

  // Click-to-tajweed-tip popover for red/missed words on the Madani page.
  const [wordTip, setWordTip] = useState(null); // {verseKey, position, expected, tip?, loading?}
  const openWordTip = async (verseKey, position, status, pageText) => {
    setWordTip({ verseKey, position, expected: pageText, status, tip: null, loading: true });
    try {
      const r = await api.post("/recitation/tajweed-tip", {
        expected: pageText,
        heard: status === "missed" ? "" : (result?.transcribed || ""),
        surah,
        ayah: parseInt(verseKey.split(":")[1]),
      });
      setWordTip((cur) => (cur && cur.verseKey === verseKey && cur.position === position
        ? { ...cur, tip: r.data.tip, loading: false }
        : cur));
    } catch (err) {
      console.error("[hifz-test] word tajweed-tip failed:", err);
      setWordTip((cur) => (cur && cur.verseKey === verseKey && cur.position === position
        ? { ...cur, tip: tr("hifzTest.couldnTFetchATip"), loading: false }
        : cur));
    }
  };

  return (
    <div className="space-y-5" data-testid="whisper-card">
      <div className="hifz-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{tr("hifzTest.aiRecitationFeedback")}</div>
            <p className="text-sm text-foreground/70 mt-1">{tr("hifzTest.reciteTheEntireRangeIn")}</p>
          </div>
          {budget && (
            budget.unlimited ? (
              <span className="status-pill status-memorized text-[11px]" data-testid="whisper-budget-unlimited">
                <Sparkles className="h-3 w-3" /> {tr("hifzTest.unlimited")}
              </span>
            ) : noAiOnPlan ? (
              <div className="text-right" data-testid="whisper-budget-none">
                <div className="text-[11px] font-semibold text-foreground/70">{tr("hifzTest.noAiOnPlan")}</div>
              </div>
            ) : (
              <div className="text-right" data-testid="whisper-budget">
                <div className={`text-[11px] font-semibold ${budget.remaining_tests <= 2 ? "text-terracotta-600" : "text-foreground/70"}`}>
                  {tr("hifzTest.testsLeft", { left: budget.remaining_tests, total: budget.limit_tests })}
                </div>
                <div className="text-[10px] text-muted-foreground" data-testid="whisper-renew-note">
                  {tr("hifzTest.testsRenew", { minutes: Math.round((budget.per_test_seconds || 300) / 60) })}
                </div>
              </div>
            )
          )}
        </div>

        {/* Hidden Mushaf canvas — Madani 15-line layout when layout='madani',
            otherwise the simple flow placeholder + per-word reveal sweep. */}
        {madaniActive ? (
          <div className="mt-5 space-y-3" data-testid="whisper-mushaf-madani">
            <MadaniPage
              pageNo={madaniPageNo}
              statusByVerseWord={statusByVerseWord}
              hiddenAyahs={hiddenAyahsSet}
              onWordClick={openWordTip}
            />
            <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="whisper-progress">
              <div className="text-xs text-foreground/75">
                {result ? (
                  <>
                    <span className="font-semibold text-forest-700">{ayahStart === ayahEnd ? tr("hifzTest.ayah", { ayahStart }) : tr("hifzTest.ayahs", { ayahStart, ayahEnd })}</span>
                    {" · "}{tr("hifzTest.accuracyMistakes", { accuracy: result.accuracy, count: (result.word_diff || []).filter((w) => w.status !== "ok").length })}
                  </>
                ) : (
                  <span className="font-semibold text-forest-700">{ayahStart === ayahEnd ? tr("hifzTest.ayah", { ayahStart }) : tr("hifzTest.ayahs", { ayahStart, ayahEnd })}</span>
                )}
              </div>
            </div>
            {lastAudioUrl && (
              <div className="rounded-xl border border-sand-300 bg-white p-3" data-testid="whisper-playback">
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
                  {tr("hifzTest.yourRecitationRange", { range: ayahStart === ayahEnd ? tr("hifzTest.rangeAyah", { n: ayahStart }) : tr("hifzTest.rangeAyahs", { start: ayahStart, end: ayahEnd }) })}
                </div>
                <audio controls src={lastAudioUrl} className="w-full" data-testid="whisper-playback-audio" />
              </div>
            )}
            {/* Submit panel — appears as soon as the range is scored. */}
            {result && (
              submitted ? (
                <div className="rounded-2xl border border-emerald-500/40 bg-emerald-50 p-4 text-center text-sm text-forest-700 font-medium" data-testid="whisper-submitted">
                  {tr("hifzTest.rangeSentToYourTeacher")}
                </div>
              ) : (
                <div className="rounded-2xl border border-forest-600/30 bg-forest-600/5 p-4 space-y-3" data-testid="whisper-submit-panel">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-forest-700 font-semibold inline-flex items-center gap-2">
                    <Award className="h-3.5 w-3.5" /> {tr("hifzTest.rangeCompleteSubmitToYour")}
                  </div>
                  <div className="grid gap-2">
                    <select
                      value={selectedClassId}
                      onChange={(e) => setSelectedClassId(e.target.value)}
                      className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm"
                      data-testid="whisper-teacher-select"
                    >
                      <option value="">{tr("hifzTest.pickAHalaqaTeacher")}</option>
                      {myClasses.map((c) => (
                        <option key={c.class_id} value={c.class_id}>
                          {c.teacher_name ? tr("hifzTest.text", { name: c.name, teacher_name: c.teacher_name }) : c.name}
                        </option>
                      ))}
                    </select>
                    <input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={tr("hifzTest.optionalNoteForYourTeacher")}
                      className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm"
                      data-testid="whisper-note"
                    />
                  </div>
                  {myClasses.length === 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      {tr("hifzTest.youHavenTJoinedA")} <Link to="/app/halaqa" className="text-forest-700 hover:underline">{tr("hifzTest.findAHalaqa")}</Link>
                    </p>
                  )}
                  <button onClick={submitToTeacher} disabled={busy} className="btn-accent text-sm w-full" data-testid="whisper-submit-teacher">
                    <Send className="h-4 w-4" /> {busy ? tr("hifzTest.sending") : tr("hifzTest.sendRangeToTeacher")}
                  </button>
                </div>
              )
            )}
            {!result && (
              <p className="text-[11px] text-center text-muted-foreground" data-testid="whisper-madani-hint">
                {tr("hifzTest.reciteRangeInOneGo", { range: ayahStart === ayahEnd ? tr("hifzTest.rangeAyah", { n: ayahStart }) : tr("hifzTest.rangeAyahs", { start: ayahStart, end: ayahEnd }) })}
              </p>
            )}
            {wordTip && (
              <div className="rounded-2xl border border-terracotta-500/40 bg-terracotta-50 p-4 text-sm" data-testid="whisper-word-tip">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-terracotta-700 font-semibold">
                      {tr("hifzTest.wordTipHeader", { kind: wordTip.status === "missed" ? tr("hifzTest.wordSkipped") : tr("hifzTest.mistake"), verseKey: wordTip.verseKey, position: wordTip.position })}
                    </div>
                    <div className="text-foreground/80">
                      {tr("hifzTest.expected")} <span className="ayah-arabic text-emerald-700 mx-1" dir="rtl">{wordTip.expected}</span>
                      {result?.transcribed && wordTip.status !== "missed" && (
                        <> {tr("hifzTest.theAiHeard")} <span className="ayah-arabic text-terracotta-600 mx-1" dir="rtl">{result.transcribed.split(/\s+/).slice(0, 8).join(" ")}…</span></>
                      )}
                    </div>
                    {wordTip.loading ? (
                      <div className="text-xs text-muted-foreground italic">{tr("hifzTest.thinking")}</div>
                    ) : (
                      <div className="text-foreground/85" data-testid="whisper-word-tip-text">{wordTip.tip}</div>
                    )}
                  </div>
                  <button onClick={() => setWordTip(null)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="whisper-word-tip-close">
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-5 min-h-[160px] rounded-2xl border border-dashed border-sand-300 bg-sand-200/30 p-6 text-right" dir="rtl" data-testid="whisper-mushaf">
            {!result ? (
              <div className="h-full min-h-[120px] flex items-center justify-center text-center" dir="ltr" data-testid="whisper-placeholder">
                <div className="space-y-1">
                  <div className="font-display font-semibold text-forest-700">{tr("hifzTest.mushafIsHidden")}</div>
                  <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                    {tr("hifzTest.reciteFromMemory", { count: rangeAyahs.length })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="ayah-arabic text-2xl md:text-3xl leading-[2.4]">
                {ayahDisplay.map((a) => (
                  <span key={a.n} data-testid={`whisper-ayah-${a.n}`}>
                    {a.words.map((w, i) => {
                      const isRevealed = w.globalIdx < revealedUpto;
                      let cls = "opacity-0 select-none";
                      if (isRevealed) {
                        if (w.status === "ok") cls = "text-emerald-700";
                        else if (w.status === "partial") cls = "text-amber-700 underline decoration-dotted decoration-amber-500";
                        else if (w.status === "wrong") cls = "text-terracotta-600 bg-terracotta-500/15 rounded px-1";
                        else cls = "text-foreground/40 line-through"; // missed
                      }
                      return (
                        <span key={`${a.n}-w-${w.globalIdx ?? i}`} className={`inline-block mx-1 transition ${cls}`}>{w.word}</span>
                      );
                    })}
                    <span className="text-gold-600 mx-1">﴿{a.n}﴾</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {overQuota || noAiOnPlan ? null : (
          <Recorder onComplete={onAudio} disabled={busy} testid="whisper-recorder" floating />
        )}
      </div>

      {noAiOnPlan && (
        <div className="hifz-card border-gold-500/40 bg-gold-500/5 flex items-center gap-3" data-testid="whisper-noai-banner">
          <Crown className="h-5 w-5 text-gold-600 shrink-0" />
          <div className="flex-1 text-sm text-foreground/80">{tr("hifzTest.aiNeedsPackage")}</div>
          <button type="button" onClick={goToPlans} className="btn-accent text-xs shrink-0" data-testid="whisper-see-plans">{tr("basicHifz.seePlans")}</button>
        </div>
      )}

      {overQuota && (
        <div className="hifz-card border-gold-500/40 bg-gold-500/5 flex items-center gap-3" data-testid="whisper-quota-banner">
          <Crown className="h-5 w-5 text-gold-600 shrink-0" />
          <div className="flex-1 text-sm text-foreground/80">
            {tr("hifzTest.usedAllTests", { total: budget.limit_tests })}
          </div>
        </div>
      )}


      {result && (
        <div className="hifz-card space-y-4" data-testid="whisper-result">
          <div className="grid gap-3">
            <div className="rounded-xl bg-sand-200/60 border border-sand-300 p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{tr("hifzTest.accuracy")}</div>
              <div className="font-display font-bold text-4xl text-forest-700 mt-1" data-testid="whisper-accuracy">{result.accuracy}%</div>
            </div>
            <div className="rounded-xl bg-sand-200/60 border border-sand-300 p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{tr("hifzTest.wordsRight2")}</div>
              <div className="font-display font-bold text-4xl text-gold-600 mt-1" data-testid="whisper-words-right">
                {result.word_accuracy ?? (totalWords ? Math.round((100 * (totalWords - mistakeCount)) / totalWords) : 0)}%
              </div>
            </div>
            <div className="rounded-xl bg-sand-200/60 border border-sand-300 p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{tr("hifzTest.mistakes")}</div>
              <div className="font-display font-bold text-4xl text-terracotta-600 mt-1" data-testid="whisper-mistakes">{mistakeCount}<span className="text-base text-muted-foreground font-normal"> / {totalWords}</span></div>
            </div>
          </div>

          {/* Show exactly what the speech engine heard, so a low score is never a black box:
              a mishearing, a clipped first word or extra words are visible at a glance. */}
          <div className="rounded-xl border border-sand-300 bg-white p-4 space-y-2" data-testid="whisper-heard">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{tr("hifzTest.whatTheAiHeard")}</div>
            <div className="ayah-arabic text-xl text-forest-700" dir="rtl" data-testid="whisper-heard-text">
              {result.transcribed || "—"}
            </div>
            {(result.extras || []).length > 0 && (
              <div className="text-xs text-terracotta-700" data-testid="whisper-extras">
                {tr("hifzTest.extraWordsHeard", { count: result.extras.length })}{" "}
                <span className="ayah-arabic text-base" dir="rtl">{result.extras.join(" ")}</span>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              {tr("hifzTest.accuracyComparesLettersAcrossThe")}
            </p>
          </div>

          {mistakeCount > 0 && (
            <div className="rounded-2xl border border-terracotta-500/30 bg-terracotta-500/5 p-4" data-testid="whisper-mistake-list">
              <div className="text-xs uppercase tracking-[0.18em] text-terracotta-700 font-semibold mb-3">
                {tr("hifzTest.wordsToReviewTap")} <em>{tr("hifzTest.why")}</em> {tr("hifzTest.forATajweedTip")}
              </div>
              <ul className="space-y-3">
                {mistakes.map((w) => (
                  <li key={w.globalIdx ?? w.word} className="flex items-start gap-3 flex-wrap" data-testid={`whisper-mistake-${w.globalIdx}`}>
                    <span className={`status-pill text-[10px] ${
                      w.status === "wrong" ? "bg-terracotta-500/15 text-terracotta-700 border-terracotta-500/40"
                        : w.status === "missed" ? "bg-foreground/10 text-foreground/70 border-foreground/20"
                          : "bg-amber-500/15 text-amber-700 border-amber-500/40"
                    } border`}>{w.status}</span>
                    <span className="ayah-arabic text-xl text-forest-700" dir="rtl">{w.word}</span>
                    {!tips[w.globalIdx] && (
                      <button
                        onClick={() => askWhy(w.globalIdx, w.word, w.status === "missed" ? "" : (result.transcribed || ""))}
                        disabled={tipBusy[w.globalIdx]}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gold-500/40 text-gold-700 hover:bg-gold-500/10 disabled:opacity-50"
                        data-testid={`whisper-why-${w.globalIdx}`}
                      >
                        <Sparkles className="h-3 w-3" /> {tipBusy[w.globalIdx] ? tr("hifzTest.thinking") : tr("hifzTest.why")}
                      </button>
                    )}
                    {tips[w.globalIdx] && (
                      <div className="basis-full mt-1 text-sm text-foreground/85 rounded-xl border border-gold-500/30 bg-gold-50 px-3 py-2" data-testid={`whisper-tip-${w.globalIdx}`}>
                        {tips[w.globalIdx]}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!madaniActive && (
            !submitted ? (
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <input
                  value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder={tr("hifzTest.optionalNoteForYourTeacher")}
                  className="flex-1 min-w-[200px] rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm"
                  data-testid="whisper-note"
                />
                <button onClick={submitToTeacher} disabled={busy} className="btn-accent text-sm" data-testid="whisper-submit-teacher">
                  <Send className="h-4 w-4" /> {tr("hifzTest.sendToMyTeacher")}
                </button>
              </div>
            ) : (
              <div className="text-sm text-forest-700 font-medium" data-testid="whisper-submitted">{tr("hifzTest.sentToYourTeacherFor")}</div>
            )
          )}
        </div>
      )}
    </div>
  );
}

const WhisperMode = WhisperHiddenMushaf; // backwards-compat alias for the picker import

/* ---------------- Auto-advance settings (per-student preference) ---------------- */
function AutoAdvanceSettings({ value, onChange }) {
  const { tx, t: tr } = useLanguage();
  const presets = [
    { ms: 0, label: "Off", desc: "Wait for me" },
    { ms: 1000, label: "1s", desc: "Aggressive" },
    { ms: 2000, label: "2s", desc: "Balanced" },
    { ms: 3500, label: "3.5s", desc: "Patient" },
  ];
  return (
    <details className="hifz-card" data-testid="autoadvance-panel" open>
      <summary className="cursor-pointer list-none flex items-center gap-2 text-sm font-medium text-forest-700">
        <SettingsIcon className="h-4 w-4" /> {tr("hifzTest.autoAdvance")} <span className="text-muted-foreground font-normal">{presets.find((p) => p.ms === value)?.label || tr("hifzTest.ms", { value })}</span>
      </summary>
      <div className="mt-4 flex flex-wrap gap-2">
        {presets.map((p) => (
          <button
            key={p.ms}
            onClick={() => onChange(p.ms)}
            data-testid={`autoadvance-${p.ms}`}
            className={`px-3 py-2 rounded-xl text-xs font-medium border transition ${
              value === p.ms ? "bg-forest-600 text-sand-100 border-forest-600" : "bg-white border-sand-300 hover:bg-sand-100"
            }`}
          >
            <div className="font-semibold">{tx(p.label)}</div>
            <div className="text-[10px] opacity-75">{tx(p.desc)}</div>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">{tr("hifzTest.liveAsrWillMarkA")}</p>
    </details>
  );
}

/* ============================== Main ============================== */
export default function HifzTest() {
  const { t: tr } = useLanguage();
  const [params] = useSearchParams();
  const { user, setUser } = useAuth();
  const [phase, setPhase] = useState("test"); // picker | test — default direct to test (no "what are we doing today" picker)
  // "peek" and "live" both resolve to the same merged self-test-and-practice
  // mode (M3) — old deep links to either one keep working.
  const initialMode = params.get("mode") === "whisper" ? "test_whisper"
    : (params.get("mode") === "peek" || params.get("mode") === "live") ? "test_peek"
      : "test_whisper"; // Whisper is now the recommended default
  const [testMode, setTestMode] = useState(initialMode);
  const [surahs, setSurahs] = useState([]);
  const initialSurah = parseInt(params.get("surah")) || 1;
  const [surah, setSurah] = useState(AVAILABLE_SURAHS.has(initialSurah) ? initialSurah : 1);
  const [data, setData] = useState(null);
  const [ayahStart, setAyahStart] = useState(parseInt(params.get("ayah")) || 1);
  const [ayahEnd, setAyahEnd] = useState(parseInt(params.get("ayah_end") || params.get("ayah")) || 1);
  const [autoAdvanceMs, setAutoAdvanceMs] = useState(
    typeof user?.settings?.asr_auto_advance_ms === "number" ? user.settings.asr_auto_advance_ms : 2000,
  );
  const [layout] = useState("madani"); // Flow layout was removed — Madani is the only mode now

  // If url specifies a mode, skip the picker
  useEffect(() => {
    if (params.get("mode") || (params.get("surah") && params.get("ayah"))) setPhase("test");
  }, [params]);

  useEffect(() => { api.get("/quran/surahs").then((r) => setSurahs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const mode = testMode === "test_whisper" ? "whisper" : "peek";
    const t = setTimeout(() => recordVisit({
      kind: testMode === "test_whisper" ? "ai_test" : "practice",
      surah, ayah: ayahStart, ayah_end: ayahEnd,
      path: `/app/hifz-test?mode=${mode}&surah=${surah}&ayah=${ayahStart}&ayah_end=${ayahEnd}`,
    }), 1500);
    return () => clearTimeout(t);
  }, [testMode, surah, ayahStart, ayahEnd]);
  useEffect(() => { api.get(`/quran/surahs/${surah}`).then((r) => setData(r.data)).catch(() => {}); }, [surah]);
  useEffect(() => {
    if (!data) return;
    const max = data.ayahs.length;
    if (ayahStart > max) setAyahStart(1);
    if (ayahEnd > max) setAyahEnd(Math.min(max, ayahStart));
    if (ayahEnd < ayahStart) setAyahEnd(ayahStart);
  }, [data, ayahStart, ayahEnd]);

  // Page picker (Madani page → ayah), so a student can find their spot by
  // page before narrowing down to an ayah range, instead of only by number.
  const lastAyahN = data?.ayahs?.[data.ayahs.length - 1]?.n;
  const { pageNo: firstPageNo } = useAyahPage(surah, 1);
  const { pageNo: lastPageNo } = useAyahPage(surah, lastAyahN);
  const pageOptions = firstPageNo && lastPageNo
    ? Array.from({ length: lastPageNo - firstPageNo + 1 }, (_, i) => firstPageNo + i)
    : null;
  const [page, setPage] = useState(null);
  const [pageAyahs, setPageAyahs] = useState(null); // ayah numbers of `surah` present on `page`

  useEffect(() => {
    if (!firstPageNo) return;
    setPage((p) => (p && lastPageNo && p >= firstPageNo && p <= lastPageNo) ? p : firstPageNo);
  }, [surah, firstPageNo, lastPageNo]);

  useEffect(() => {
    if (!page) { setPageAyahs(null); return; }
    api.get(`/quran/page/${page}`).then((r) => {
      const ns = (r.data.verses || []).filter((v) => v.surah === surah).map((v) => v.ayah).sort((a, b) => a - b);
      setPageAyahs(ns.length ? ns : null);
      if (ns.length && !(ayahStart >= ns[0] && ayahEnd <= ns[ns.length - 1])) {
        setAyahStart(ns[0]);
        setAyahEnd(ns[0]);
      }
    }).catch(() => setPageAyahs(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, surah]);

  // Persist auto-advance preference to server (debounced via change)
  const saveAutoAdvance = async (ms) => {
    setAutoAdvanceMs(ms);
    try {
      await api.put("/users/me/settings", { asr_auto_advance_ms: ms });
      setUser?.((u) => (u ? { ...u, settings: { ...(u.settings || {}), asr_auto_advance_ms: ms } } : u));
    } catch (err) { console.debug("[hifz-test] non-fatal:", err); }
  };

  const rangeAyahs = useMemo(
    () => (data?.ayahs || []).filter((a) => a.n >= ayahStart && a.n <= ayahEnd),
    [data, ayahStart, ayahEnd],
  );

  if (phase === "picker") {
    return (
      <ModePicker
        onPick={(id) => {
          if (id === "test_peek" || id === "test_whisper") {
            setTestMode(id);
            setPhase("test");
          }
        }}
      />
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Back to wherever the test was opened from (a surah page, Revision,
            the dashboard…); the Hifz Plan is only the fallback for a deep link. */}
        <BackLink to="/app/hifz" label={tr("hifzTest.back")} className="text-xs text-foreground/70 hover:underline inline-flex items-center gap-1" testid="hifz-back-to-plan" />
        <div className="inline-flex gap-1 p-1 rounded-full bg-sand-200 border border-sand-300" data-testid="hifz-mode-tabs">
          {[
            ["test_peek", tr("hifzTest.selfTestPractice"), Brain],
            ["test_whisper", tr("revision.aiTest"), Headphones],
          ].map(([v, l, Icon]) => (
            <button key={v} onClick={() => setTestMode(v)} data-testid={`tab-${v}`}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition ${
                testMode === v ? "bg-forest-600 text-sand-100" : "text-foreground/70 hover:text-foreground"
              }`}>
              <Icon className="h-3.5 w-3.5" /> {l}
            </button>
          ))}
        </div>
      </div>

      <RangeControls
        surahs={surahs} surah={surah} setSurah={setSurah} data={data}
        page={page} setPage={setPage} pageOptions={pageOptions} pageAyahs={pageAyahs}
        ayahStart={ayahStart} setAyahStart={setAyahStart}
        ayahEnd={ayahEnd} setAyahEnd={setAyahEnd}
        onReset={() => { setPage(firstPageNo || null); setAyahStart(1); setAyahEnd(1); }}
      />

      {testMode === "test_whisper"
        ? <WhisperMode rangeAyahs={rangeAyahs} surah={surah} ayahStart={ayahStart} ayahEnd={ayahEnd} layout={layout} />
        : <SelfTestMode rangeAyahs={rangeAyahs} surah={surah} autoAdvanceMs={autoAdvanceMs} onChangeAutoAdvance={saveAutoAdvance} layout={layout} />}
    </div>
  );
}
